import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FilterGroupingEnum,
  useFormTemplateUnlinkAllDocumentationsMutation,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import LinkedDocuments from '@lib/components/LinkedDocuments';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import toast from '@lib/components/Toast/Toast';
import Trans from '@lib/components/Trans/Trans';
import Typography from '@lib/components/Typography/Typography';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import { IconName } from '@lib/components/Modal/enums';
import { PlanState } from '@lib/enums/plan';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';

interface Props {
  formTemplateId: string;
  hasLinkedDocumentations?: boolean;
  onUnlinked?: () => void;
}

export default function FormTemplateLinkedDocumentsSection(props: Props) {
  const { formTemplateId, hasLinkedDocumentations, onUnlinked } = props;
  const { t } = useTranslation();
  const {
    getUserAccessByCompany,
    handleOpenPlanModal,
    isFullAdmin,
  }: ContextProps = useOutletContext();
  const isViewLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const [isUnlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);

  const [formTemplateUnlinkAllDocumentations, { loading: unlinkLoading }] =
    useFormTemplateUnlinkAllDocumentationsMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  const onCloseUnlinkConfirm = useCallback(() => {
    setUnlinkConfirmOpen(false);
  }, []);

  const onConfirmUnlink = useCallback(() => {
    formTemplateUnlinkAllDocumentations({
      variables: { input: { id: formTemplateId } },
      onCompleted: () => {
        toast({ content: t('unlink-documents-from-form-template-success') });
        setUnlinkConfirmOpen(false);
        onUnlinked?.();
      },
    });
  }, [formTemplateId, formTemplateUnlinkAllDocumentations, onUnlinked, t]);

  if (!isViewLocked) {
    return (
      <div
        className={classNames(
          entityViewStyles.infoItem,
          entityViewStyles.block,
        )}
      >
        <p className={entityViewStyles.infoItemLabel}>
          <MaterialIcon icon="attach_file" symbolsOutlined />
          <Typography variant="caption">{t('linked-documents')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContentFullWidth}>
          <LinkedDocuments
            filters={[
              {
                linkedFormTemplatesId: {
                  grouping: FilterGroupingEnum.And,
                  predicate: { in: [formTemplateId] },
                },
              },
            ]}
            getDetailsUrl={(doc) =>
              APP_URLS.dashboard.documentation.view.getDynamicPath({
                pathParts: { id: doc.id },
              })
            }
          />
        </div>
      </div>
    );
  }

  if (!isFullAdmin) return null;
  // Hide the lock-state panel once the BE confirms there are no linked
  // docs left to clean up (e.g. right after a successful unlink).
  if (!hasLinkedDocumentations) return null;

  return (
    <>
      <div
        className={classNames(
          entityViewStyles.infoItem,
          entityViewStyles.block,
        )}
      >
        <p className={entityViewStyles.infoItemLabel}>
          <MaterialIcon icon="attach_file" symbolsOutlined />
          <Typography variant="caption">{t('linked-documents')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContentFullWidth}>
          <SupportiveMessage
            text={t('advanced-documentation-features-not-in-plan')}
            color={SupportiveMessageColor.Yellow}
            type={SupportiveMessageType.FilledWithLeftBorder}
            actions={
              <>
                <Button
                  buttonText={t('unlink-document')}
                  buttonType={ButtonTypes.primaryText}
                  onClick={() => setUnlinkConfirmOpen(true)}
                />
                <Button
                  buttonText={t('upgrade-plan')}
                  buttonType={ButtonTypes.primaryFilled}
                  onClick={() =>
                    handleOpenPlanModal(PlanState.Locked, [
                      CompanyPlanUpgradeFeatureEnum.Documentation,
                    ])
                  }
                />
              </>
            }
          />
        </div>
      </div>
      <ConfirmationModal
        confirmButtonLabel={t('yes-unlink')}
        description={
          <Trans i18nKey="unlink-documents-from-this-form-template-text" />
        }
        icon={IconName.LinkOff}
        isOpen={isUnlinkConfirmOpen}
        loading={unlinkLoading}
        onClose={onCloseUnlinkConfirm}
        onConfirm={onConfirmUnlink}
        title={t('unlink-documents-from-this-form-template')}
        useProgress
      />
    </>
  );
}
